import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { DragEndEvent } from '@dnd-kit/core';
import { skipToken } from '@reduxjs/toolkit/query/react';
import classNames from 'classnames';

import { WeeklyPlannerDateRangeOptions as RangeOptions } from '~constants/maps';

import { Appointment, getGridColumn, UnallocatedAppointment } from '~weekly-planner/lib/common';
import { onUnallocatedDragEnd } from '~weekly-planner/lib/drag-drop';

import { useFocusAppointment } from '~weekly-planner/hooks/useFocusAppointment';

import Button from '~components/Button';
import ClickableIcon from '~components/ClickableIcon';
import { DraggableItem, DroppableArea } from '~components/drag-and-drop';
import Icon, {
  faChevronLeft,
  faChevronRight,
  faEdit,
  faExclamationTriangle,
  faInfoCircle,
  faMagnifyingGlass,
  faUserPen,
} from '~components/icon';
import PaginatedList from '~components/PaginatedList';
import Spinner from '~components/Spinner';
import UnallocatedCard from '~weekly-planner/components/card/UnallocatedCard';
import ConfirmBulkAssign from '~weekly-planner/components/modal/ConfirmBulkAssign';
import Search from '~weekly-planner/components/sidebar/unallocated-appointments/Search';
import UnallocatedViewMenu from '~weekly-planner/components/sidebar/unallocated-appointments/UnallocatedViewMenu';

import { bulkUpdate, update as updateUnallocated } from '~appointments/actions/appointments';
import { useGetAssignableUsersQuery } from '~weekly-planner/actions/appointments';
import { unallocatedApi, useGetUnallocatedQuery } from '~weekly-planner/actions/appointments/unallocated';

import { updateParams } from '~weekly-planner/reducers/appointments/unallocated';
import { clear as clearUsers } from '~weekly-planner/reducers/users/users';
import { patchParams } from '~weekly-planner/reducers/weeklyPlanner';

import {
  selectDerivedDateRange,
  selectFocusedAppointment,
  selectParams as selectGlobalParams,
  selectPlannerAPIOptions,
} from '~weekly-planner/selectors';
import {
  selectAdjustedUnallocatedCount,
  selectParams,
  selectUnallocated,
} from '~weekly-planner/selectors/appointments/unallocated';
import { selectRenderable as selectSimulatedChanges } from '~weekly-planner/selectors/users';

import UnallocatedAppointmentEditModal from './UnallocatedAppointmentEditModal';

interface Filters {
  client_id: number | null;
  user_id: number | string | null;
  suburb: string | null;
  date: string | null;
}

interface ComponentProps {
  isOpen: boolean;
}

const UnallocatedAppointments: React.FC<ComponentProps> = React.memo(({ isOpen }) => {
  const dispatch = useAppDispatch();
  const globalParams = useAppSelector(selectGlobalParams);
  const params = useAppSelector(selectParams);
  const plannerAPIOptions = useAppSelector(selectPlannerAPIOptions);
  const derivedDateRange = useAppSelector(selectDerivedDateRange);
  const appointments = useAppSelector(selectUnallocated);
  const weeklyPlannerData = useAppSelector(selectSimulatedChanges);
  const focused = useAppSelector(selectFocusedAppointment);
  const count = useAppSelector(selectAdjustedUnallocatedCount);

  const { focus: focusAppointment, unfocusAndRestoreView: unfocusAppointment } = useFocusAppointment();

  const { week_start } = derivedDateRange;
  const { viewType } = globalParams;
  const { limit } = params;
  const [openAppointment, setOpenAppointment] = useState<number | null>(null);
  const [openEditAppointment, setOpenEditAppointment] = useState<Appointment | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<Filters>({
    client_id: null,
    user_id: null,
    suburb: null,
    date: null,
  });
  const [isOver, setIsOver] = useState(false);
  const [isOrigin, setIsOrigin] = useState(false);
  const [isBulkAssignOpen, setIsBulkAssignOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const { isFetching } = useGetUnallocatedQuery(
    params?.week_start && isOpen ? { limit, week_start: params?.week_start } : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const hasMore = count !== appointments.length;

  // Add ref for the top of the container
  const topRef = useRef<HTMLDivElement>(null);

  const onLoadMore = () => {
    if (!isFetching && appointments.length < count) {
      dispatch(updateParams({ ...params, limit: (params.limit ?? 30) + 30 }));
    }
  };

  useEffect(() => {
    if (isOpen && week_start) {
      const params = { week_start, limit: 30 };
      dispatch(updateParams(params));
    }
  }, [isOpen, week_start]);

  useMemo(() => {
    if (!focused && openAppointment) setOpenAppointment(null);
  }, [focused]);

  const handleDragOverChange = (isOver: boolean, current?: any) => {
    setIsOver(isOver);
    setIsOrigin(current?.containerId === 'unallocated-appointments');
  };

  const { suburb, ...remainingFilters } = filters;
  useEffect(() => {
    dispatch(
      updateParams({
        ['client.suburb']: {
          like: suburb ? `%${suburb}%` : undefined,
        },
        ...remainingFilters,
      }),
    );
  }, [filters]);

  const focusedAppointmentId = focused?.appointment?.id ?? null;
  useGetAssignableUsersQuery(focusedAppointmentId ? { id: String(focusedAppointmentId) } : skipToken);

  const onUnallocatedAppointmentFocus = (key: number | null) => {
    if (!key) return;
    if (openAppointment === key) {
      dispatch(clearUsers([]));
      unfocusAppointment();
    } else {
      dispatch(clearUsers([]));
      const appointment = appointments.find((appt) => appt.key === key);
      dispatch(patchParams({ date: appointment?.date, rangeType: RangeOptions.DAILY }));
      focusAppointment({
        appointment,
        isAvailabilityView: true,
        isBiddingView: appointment?.is_bidding,
        newBidders: [],
      });
    }
    setOpenAppointment((prev) => (prev === key ? null : key));
  };

  const onEditAppointment = (appointment: Appointment | null) => {
    setOpenEditAppointment(appointment);
    setIsEditOpen(true);
  };

  const onSaveEditModal = async (values: any) => {
    if (openEditAppointment?.id === undefined || openEditAppointment?.id === null) {
      const finalValues = {
        id: null,
        user_id: null,
        ...values,
        // Populate required fields based on the opened appointment
        date: values.date ?? openEditAppointment?.date,
        parent_repeat_id: openEditAppointment?.parent_repeat_id,
      };
      const { payload } = await dispatch(bulkUpdate([finalValues]));
      if (payload?.success) {
        dispatch(unallocatedApi.util.invalidateTags([{ type: 'unallocated', id: 'LIST' }]));
        setIsEditOpen(false);
        setOpenEditAppointment(null);
      }
    } else {
      const { payload } = await dispatch(
        updateUnallocated({
          id: openEditAppointment?.id,
          ...values,
        }),
      );
      if (payload?.success) {
        dispatch(unallocatedApi.util.invalidateTags([{ type: 'unallocated', id: 'LIST' }]));
        setIsEditOpen(false);
        setOpenEditAppointment(null);
      }
    }
  };

  const renderAppointment = (appointment: UnallocatedAppointment, index: number) => {
    const { key } = appointment;
    if (!key) return <></>;

    const style: CSSProperties = {
      gridRow: 1,
      ...getGridColumn(appointment.start_time, appointment.end_time, 1),
    };

    const isDragDisabled = viewType === 'Clients';

    return (
      <div key={index} className="unallocated-row">
        <div className="unallocated-card-container">
          <DraggableItem
            id={key}
            disabled={isDragDisabled}
            data={{
              appointment,
              containerId: 'unallocated-appointments',
              containerStyle: style,
              onItemDragEnd: (event: DragEndEvent) => onUnallocatedDragEnd(event, plannerAPIOptions),
              weeklyPlannerData,
              week_start,
              dispatch,
            }}
          >
            <UnallocatedCard
              appointment={appointment}
              isActionMenuOpen={openAppointment === appointment.key}
              anyActionMenuOpen={!!openAppointment}
            />
          </DraggableItem>
        </div>
        <div className="action-menu-container d-flex flex-column">
          <Button className="action-toggle rounded-0" onClick={() => onEditAppointment(appointment)}>
            <Icon icon={faEdit} />
          </Button>
          <Button className="action-toggle rounded-0" onClick={() => onUnallocatedAppointmentFocus(appointment.key)}>
            <Icon className="me-2" icon={openAppointment === appointment.key ? faChevronLeft : faChevronRight} />
          </Button>
        </div>
      </div>
    );
  };

  const className = classNames('card-container unallocated-appointments inner-content limited', {
    'is-dragging-over': isOver,
    'is-dragging-origin': isOrigin,
  });

  const loader = <Spinner loading={true} className="p-5" />;
  const endMessage = (
    <p className="no-result">
      <Icon icon={faInfoCircle} />
      No more unallocated appointments found
    </p>
  );
  const emptyMessage = (
    <p className="no-result">
      <Icon icon={faExclamationTriangle} />
      No results found
    </p>
  );

  const handleSearchClick = () => {
    const newSearchState = !isSearchOpen;
    setIsSearchOpen(newSearchState);

    // If opening the search, scroll to the top ref
    if (newSearchState) {
      setTimeout(() => {
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    }
  };

  const ClientViewBanner: React.FC<{ visible: boolean }> = ({ visible }) => {
    if (!visible) return null;

    return (
      <div className="small text-muted text-start justify-self-start">
        * Drag and drop is disabled in Client View Mode
      </div>
    );
  };
  const navClassName = classNames('nav', 'sidebar-list', {
    'unallocated-header': viewType === 'Clients',
  });
  return (
    <div className="sidebar-list">
      <ConfirmBulkAssign
        isOpen={isBulkAssignOpen}
        setIsOpen={setIsBulkAssignOpen}
        onConfirm={() => {
          dispatch(updateParams({ ...params, limit: 30 }));
        }}
      />
      <DroppableArea id="unallocated-appointments" onDragOverChange={handleDragOverChange} className={className}>
        {/* Empty div ref for handling scroll to top on open of search */}
        <div ref={topRef} style={{ position: 'absolute', top: '50px' }}></div>
        <div className={navClassName}>
          <ClientViewBanner visible={viewType === 'Clients'} />
          <div className="d-flex flex-row align-items-center">
            <ClickableIcon
              id="search-appointments"
              icon={faMagnifyingGlass}
              toolTip="Search Appointments"
              onClick={handleSearchClick}
              className="ms-2"
            />
            <ClickableIcon
              id="bulk-assign"
              icon={faUserPen}
              toolTip="Assign Preferred Workers"
              onClick={() => setIsBulkAssignOpen(true)}
              className="ms-2"
            />
            <UnallocatedViewMenu />
          </div>
        </div>
        <Search isOpen={isSearchOpen} weekStart={week_start} setFilters={setFilters} />
        <PaginatedList<UnallocatedAppointment>
          items={appointments}
          renderItem={renderAppointment}
          loadMore={onLoadMore}
          hasMore={hasMore}
          isLoading={isFetching}
          loader={loader}
          endMessage={endMessage}
          emptyMessage={emptyMessage}
          buttonText="Load More Appointments"
          buttonClassName="btn btn-primary d-block mx-auto p-2 m-2"
          className="unallocated-appointments-container"
        />
      </DroppableArea>
      <UnallocatedAppointmentEditModal
        isOpen={isEditOpen}
        setIsOpen={setIsEditOpen}
        appointment={openEditAppointment}
        onSave={onSaveEditModal}
      />
    </div>
  );
});

UnallocatedAppointments.displayName = 'UnallocatedAppointments';
export default UnallocatedAppointments;
